import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const jwtSchema = z
  .string()
  .min(1)
  .regex(/^[\w-]+\.[\w-]+\.[\w-]+$/, "Invalid JWT format");

// ── Payload Types ─────────────────────────────────────────
export interface ArchiveEntry {
  id: string;
  original_file_path: string;
  drive_file_id: string | null;
  file_size_bytes: number;
  mime_type: string | null;
  archived_at: string;
  archived_by: string;
}

export interface ArchiveStatsPayload {
  totalArchived: number;
  totalSizeBytes: number;
  recentArchives: ArchiveEntry[];
}

// ── Server Functions ──────────────────────────────────────
export const getArchiveStats = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      accessToken: jwtSchema,
    })
  )
  .handler(async ({ data }) => {
    const { createAdminClient } = await import("./_admin-utils.server");
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError ?? "Missing server env vars" };

    const { data: { user }, error: authError } = await client.auth.getUser(data.accessToken);
    if (authError || !user) return { data: null, error: "Unauthorized" };

    const { data: archives, error, count } = await client
      .from("archived_attachments")
      .select("id, original_file_path, drive_file_id, file_size_bytes, mime_type, archived_at, archived_by", { count: "exact" })
      .order("archived_at", { ascending: false })
      .limit(10);

    if (error) return { data: null, error: error.message };

    const list = (archives ?? []) as Array<Record<string, unknown>>;
    const totalSizeBytes = list.reduce((sum, a) => sum + (Number(a.file_size_bytes) || 0), 0);

    const recentArchives: ArchiveEntry[] = list.map((a) => ({
      id: String(a.id),
      original_file_path: String(a.original_file_path),
      drive_file_id: a.drive_file_id ? String(a.drive_file_id) : null,
      file_size_bytes: Number(a.file_size_bytes) || 0,
      mime_type: a.mime_type ? String(a.mime_type) : null,
      archived_at: String(a.archived_at),
      archived_by: String(a.archived_by),
    }));

    return {
      data: {
        totalArchived: count ?? 0,
        totalSizeBytes,
        recentArchives,
      },
      error: null,
    };
  });
